<template>
  <transition name="cp-fade">
    <div v-if="isOpen" class="cp-overlay" @mousedown.self="close">
      <div class="cp-panel" role="dialog" aria-label="Command Palette">
        <div class="cp-input-wrapper">
          <feather-icon icon="SearchIcon" svgClasses="w-5 h-5 text-grey" />
          <input
            ref="input"
            v-model="query"
            type="text"
            class="cp-input"
            placeholder="Buscar páginas, acciones..."
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="select"
            @keydown.esc.prevent="close"
          />
          <span class="cp-hint">ESC</span>
        </div>

        <div class="cp-results" ref="results">
          <template v-if="filtered.length">
            <div
              v-for="(item, idx) in filtered"
              :key="item.id"
              :ref="idx === activeIndex ? 'active' : null"
              class="cp-item"
              :class="{ 'cp-item--active': idx === activeIndex }"
              @mousemove="activeIndex = idx"
              @mousedown.prevent="run(item)"
            >
              <feather-icon
                :icon="item.icon || 'CircleIcon'"
                svgClasses="w-4 h-4"
                class="cp-item-icon"
              />
              <div class="cp-item-body">
                <div class="cp-item-title">{{ item.title }}</div>
                <div v-if="item.subtitle" class="cp-item-subtitle">{{ item.subtitle }}</div>
              </div>
              <span class="cp-item-group">{{ item.group }}</span>
            </div>
          </template>
          <div v-else class="cp-empty">Sin resultados para "{{ query }}"</div>
        </div>

        <div class="cp-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>↵</kbd> abrir</span>
          <span><kbd>esc</kbd> cerrar</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import navMenuItems from '@/layouts/components/vertical-nav-menu/navMenuItems.js'

export default {
  name: 'CommandPalette',
  data () {
    return {
      isOpen: false,
      query: '',
      activeIndex: 0,
      items: []
    }
  },
  computed: {
    filtered () {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.items
      // Substring match on title + group, with simple ranking:
      // title startsWith > title includes > group includes
      const scored = []
      for (const it of this.items) {
        const title = it.title.toLowerCase()
        const group = (it.group || '').toLowerCase()
        let score = -1
        if (title.startsWith(q)) score = 3
        else if (title.includes(q)) score = 2
        else if (group.includes(q)) score = 1
        if (score >= 0) scored.push({ it, score })
      }
      scored.sort((a, b) => b.score - a.score)
      return scored.map(s => s.it)
    }
  },
  watch: {
    query () {
      this.activeIndex = 0
    },
    isOpen (val) {
      if (val) {
        this.query = ''
        this.activeIndex = 0
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus()
        })
      }
    }
  },
  methods: {
    open () {
      this.isOpen = true
    },
    close () {
      this.isOpen = false
    },
    toggle () {
      this.isOpen ? this.close() : this.open()
    },
    move (delta) {
      const len = this.filtered.length
      if (!len) return
      this.activeIndex = (this.activeIndex + delta + len) % len
      this.$nextTick(() => {
        const el = this.$refs.active && this.$refs.active[0]
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
    select () {
      const item = this.filtered[this.activeIndex]
      if (item) this.run(item)
    },
    run (item) {
      this.close()
      if (item.action) {
        item.action(this)
        return
      }
      if (item.routeName && this.$route.name !== item.routeName) {
        this.$router.push({ name: item.routeName }).catch(() => {})
      } else if (item.path && this.$route.path !== item.path) {
        this.$router.push(item.path).catch(() => {})
      }
    },
    handleGlobalKey (e) {
      const isMeta = e.ctrlKey || e.metaKey
      if (isMeta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        this.toggle()
      }
    },
    buildItems () {
      const items = []
      let idCounter = 0
      const nextId = () => `cp-${++idCounter}`

      // Spanish labels keyed by route path. Overrides whatever label
      // navMenuItems carries (which is in English / i18n keys).
      const NAV_LABELS_ES = {
        '/sales/quotes':        { title: 'Cotizaciones',          group: 'Ventas' },
        '/sales/purchases':     { title: 'Órdenes de compra',     group: 'Ventas' },
        '/sales/guides':        { title: 'Guías de remisión',     group: 'Ventas' },
        '/sales/invoices':      { title: 'Comprobantes de venta', group: 'Ventas' },
        '/sales/credits':       { title: 'Notas de crédito',      group: 'Ventas' },
        '/purchases/invoices':  { title: 'Comprobantes de compra', group: 'Compras' },
        '/clients/list':        { title: 'Clientes',              group: 'Clientes' },
        '/clients/addresses':   { title: 'Direcciones de clientes', group: 'Clientes' },
        '/clients/contacts':    { title: 'Contactos de clientes', group: 'Clientes' },
        '/providers/list':      { title: 'Proveedores',           group: 'Proveedores' },
        '/providers/addresses': { title: 'Direcciones de proveedores', group: 'Proveedores' },
        '/providers/contacts':  { title: 'Contactos de proveedores', group: 'Proveedores' },
        '/warehouse/products':  { title: 'Productos',             group: 'Almacén' },
        '/warehouse/warehouses':{ title: 'Almacenes',             group: 'Almacén' },
        '/warehouse/vehicles':  { title: 'Vehículos',             group: 'Almacén' },
        '/warehouse/inputs':    { title: 'Entradas',              group: 'Movimientos' },
        '/warehouse/outputs':   { title: 'Salidas',               group: 'Movimientos' },
        '/warehouse/transfers': { title: 'Transferencias',        group: 'Movimientos' },
        '/warehouse/movements': { title: 'Movimientos',           group: 'Movimientos' },
        '/warehouse/queries':   { title: 'Consultas',             group: 'Movimientos' },
        '/settings/users':      { title: 'Usuarios',              group: 'Configuración' },
        '/settings/content':    { title: 'Configuración general', group: 'Configuración' }
      }

      // 1) Navigation items from the existing nav menu
      for (const section of navMenuItems) {
        const fallbackGroup = section.i18n || section.header || ''
        const children = section.items || (section.submenu || [])
        for (const child of children) {
          if (!child.url) continue
          const override = NAV_LABELS_ES[child.url]
          items.push({
            id: nextId(),
            title: (override && override.title) || child.i18n || child.name || child.url,
            group: (override && override.group) || fallbackGroup,
            icon: child.icon,
            path: child.url
          })
        }
      }

      // 2) Quick "create new" actions — navigate to the listing for now.
      //    Later these can dispatch a Vuex flag so the listing opens its New sidebar.
      const creators = [
        { title: 'Nueva venta', path: '/sales/invoices', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nueva cotización', path: '/sales/quotes', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nueva guía de remisión', path: '/sales/guides', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nueva nota de crédito', path: '/sales/credits', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nueva orden de compra', path: '/sales/purchases', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nuevo comprobante de compra', path: '/purchases/invoices', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nuevo cliente', path: '/clients/list', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nuevo proveedor', path: '/providers/list', icon: 'PlusIcon', group: 'Acciones' },
        { title: 'Nuevo producto', path: '/warehouse/products', icon: 'PlusIcon', group: 'Acciones' }
      ]
      for (const c of creators) items.push({ id: nextId(), ...c })

      this.items = items
    }
  },
  mounted () {
    this.buildItems()
    window.addEventListener('keydown', this.handleGlobalKey)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleGlobalKey)
  }
}
</script>

<style scoped>
.cp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 53000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
}

.cp-panel {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: inherit;
}

.cp-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid #eef0f4;
}

.cp-input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: #1f2937;
}

.cp-hint {
  font-size: 11px;
  color: #94a3b8;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 2px 6px;
}

.cp-results {
  max-height: 50vh;
  overflow-y: auto;
  padding: 6px;
}

.cp-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
}

.cp-item--active {
  background: #f1f5fe;
}

.cp-item-icon {
  color: #64748b;
  flex-shrink: 0;
}

.cp-item--active .cp-item-icon {
  color: #2563eb;
}

.cp-item-body {
  flex: 1;
  min-width: 0;
}

.cp-item-title {
  font-size: 14px;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cp-item-subtitle {
  font-size: 12px;
  color: #64748b;
}

.cp-item-group {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.cp-empty {
  padding: 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.cp-footer {
  display: flex;
  gap: 16px;
  padding: 10px 18px;
  border-top: 1px solid #eef0f4;
  font-size: 11px;
  color: #94a3b8;
  background: #fafbfc;
}

.cp-footer kbd {
  display: inline-block;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 4px;
  font-family: inherit;
  font-size: 10px;
  color: #475569;
}

/* Dark theme */
.theme-dark .cp-panel {
  background: #10163a;
  color: #e2e8f0;
}
.theme-dark .cp-input { color: #e2e8f0; }
.theme-dark .cp-input-wrapper { border-color: #1e2756; }
.theme-dark .cp-item-title { color: #e2e8f0; }
.theme-dark .cp-item--active { background: #1e2756; }
.theme-dark .cp-footer { background: #0b1230; border-color: #1e2756; }
.theme-dark .cp-footer kbd { background: #1e2756; border-color: #2d3a73; color: #cbd5e1; }

.cp-fade-enter-active, .cp-fade-leave-active {
  transition: opacity 0.12s ease;
}
.cp-fade-enter, .cp-fade-leave-to {
  opacity: 0;
}
</style>
